import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../API';
import { useAuth } from '../../providers/AuthContext';
import ProjectItemListWithProgress from './ProjectItemListWithProgress';
import {
    PROJECT_LIFECYCLE,
    normalizeProjectLifecycleStatus,
} from './projectLifecycle';
import {
    getSurveyByType,
    getSurveyDisplayTitle,
    getSurveyTypeLabel,
    normalizeSurveysConfig,
    SURVEY_TYPE_ORDER,
    SURVEY_TYPES,
} from './questions';
import RoleBadge from './roleUtils';
import ProjectsHubBackLink from './hub/ProjectsHubBackLink';
import styles from './ProjectHomepage.module.css';

function ProjectHomepage() {
    const { folderId } = useParams();
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();

    const [doc, setDoc] = useState(null);
    const [items, setItems] = useState([]);
    const [firstItemId, setFirstItemId] = useState(null);
    const [progressMap, setProgressMap] = useState({});
    const [surveyStatus, setSurveyStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    useEffect(() => {
        setLoading(true);
        setError(null);

        const projectPromise = api.get(`projects/${folderId}`, {});
        const itemsPromise = api.get(`projects/${folderId}/items`, {});

        Promise.all([projectPromise, itemsPromise])
            .then(([projectRes, itemsRes]) => {
                if (projectRes.status === 200) {
                    setDoc(projectRes.data);
                } else {
                    setError(projectRes.data?.message || 'Failed to load project.');
                }
                // Items may be 403 when project is draft and user is not admin
                const itemList = itemsRes.status === 200 ? (itemsRes.data?.items ?? []) : [];
                setItems(itemList);
                if (itemList.length > 0) {
                    setFirstItemId(String(itemList[0]._id));
                }
                // Fetch progress for annotators so "Your progress" can show status
                if (projectRes.status === 200 && itemList.length > 0 && currentUser) {
                    const participants = projectRes.data?.girderProjects?.participants ?? [];
                    const myEntry = participants.find((p) => String(p.userId) === String(currentUser._id));
                    const isAnnotator = myEntry?.roles?.includes('annotator');
                    if (isAnnotator) {
                        api.get(`projects/${folderId}/progress`, {})
                            .then((res) => {
                                if (res.status === 200 && Array.isArray(res.data?.progress)) {
                                    const map = Object.fromEntries(
                                        res.data.progress.map((p) => [p.itemId, p.status || 'not_started']),
                                    );
                                    setProgressMap(map);
                                }
                            })
                            .catch(() => {});
                    }
                }
                const surveys = projectRes.data?.girderProjects?.surveys;
                if (
                    projectRes.status === 200
                    && currentUser
                    && Array.isArray(surveys)
                    && surveys.length > 0
                ) {
                    api.get(`projects/${folderId}/surveys/status`, {})
                        .then((res) => {
                            if (res.status === 200) setSurveyStatus(res.data);
                        })
                        .catch(() => {});
                } else {
                    setSurveyStatus(null);
                }
            })
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [folderId, currentUser]);

    if (loading) return <div className={styles.loading}>Loading project…</div>;

    if (error) {
        return (
            <div>
                <div className={styles.errorMessage}>{error}</div>
                <ProjectsHubBackLink className={styles.backLink} variant="long" />
            </div>
        );
    }

    if (!doc) {
        return (
            <div>
                <p>Project not found.</p>
                <ProjectsHubBackLink className={styles.backLink} variant="long" />
            </div>
        );
    }

    const gp = doc.girderProjects ?? {};
    const lifecycleStatus = normalizeProjectLifecycleStatus(gp.lifecycleStatus);
    const isDraft = lifecycleStatus === PROJECT_LIFECYCLE.DRAFT;
    const isClosed = lifecycleStatus === PROJECT_LIFECYCLE.CLOSED;
    const isArchived = lifecycleStatus === PROJECT_LIFECYCLE.ARCHIVED;
    const isPublished = lifecycleStatus === PROJECT_LIFECYCLE.PUBLISHED;
    const hasUI = !!gp.annotationUI?.id;
    const hasItems = items.length > 0;
    const isUnconfigured = !hasUI || !hasItems;

    const instructions = gp.annotationUI?.config?.instructions ?? '';
    const participants = gp.participants ?? [];
    const myUserId = currentUser ? String(currentUser._id) : null;
    const myEntry = participants.find((p) => String(p.userId) === myUserId);
    const myRoles = myEntry?.roles ?? [];

    const isAdmin = myRoles.includes('admin') || currentUser?.admin;
    const isAnnotator = myRoles.includes('annotator');
    const isReviewer = myRoles.includes('reviewer');

    const surveys = normalizeSurveysConfig(gp.surveys);
    const preTestConfigured = Boolean(getSurveyByType(surveys, SURVEY_TYPES.PRE_TEST));
    const preTestComplete = surveyStatus
        ? Boolean(surveyStatus.preTestComplete)
        : !preTestConfigured;
    const preTestBlocksViewer = isAnnotator && isPublished && preTestConfigured && !preTestComplete;

    const canOpen = isAnnotator && !!firstItemId && isPublished && !preTestBlocksViewer;
    const projectNotReadyForParticipant = !isPublished || !hasUI || !hasItems;

    function surveyLinkMeta(type) {
        const survey = getSurveyByType(surveys, type);
        if (!survey) return null;
        const byType = surveyStatus?.byType?.[type];
        const accessible = byType ? Boolean(byType.accessible) : type !== SURVEY_TYPES.POST_TEST;
        const complete = byType ? Boolean(byType.complete) : false;
        return {
            survey,
            type,
            accessible: accessible || complete,
            complete,
            href: `/projects/${folderId}/survey/${type}`,
            title: getSurveyDisplayTitle(survey),
            typeLabel: getSurveyTypeLabel(type),
        };
    }

    const surveyLinks = (isAnnotator && isPublished) || isAdmin
        ? SURVEY_TYPE_ORDER.map(surveyLinkMeta).filter(Boolean)
        : [];

    function openViewer(itemId) {
        if (preTestBlocksViewer) return;
        navigate(`/projects/${folderId}/view/${itemId}`);
    }

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.pageHeader}>
                <div className={styles.headerLeft}>
                    <ProjectsHubBackLink className={styles.backLink} variant="short" />
                    <h3 className={styles.pageTitle}>{doc.name}</h3>
                    {myRoles.length > 0 && (
                        <div className={styles.myRoles}>
                            {myRoles.map((r) => (
                                <RoleBadge
                                    key={r}
                                    role={r}
                                    variant="long"
                                    className={`${styles.roleBadge} ${styles[`roleBadge_${r}`] || ''}`}
                                />
                            ))}
                        </div>
                    )}
                </div>
                {isAdmin && (
                    <div className={styles.headerActions}>
                        <button
                            type="button"
                            className={styles.buttonSecondary}
                            onClick={() => navigate(`/projects/${folderId}`)}
                        >
                            ✎ Manage project
                        </button>
                    </div>
                )}
            </div>

            {/* Admin: draft or unconfigured empty-state card */}
            {isAdmin && (isDraft || isUnconfigured) && (
                <div className={styles.emptyStateCard}>
                    <h4 className={styles.emptyStateTitle}>
                        {isDraft
                            ? 'This project is in draft mode'
                            : isClosed
                                ? 'This project is closed'
                                : isArchived
                                    ? 'This project is archived'
                                    : 'This project needs setup'}
                    </h4>
                    <ul className={styles.emptyStateList}>
                        {!hasUI && <li>Choose an annotation UI in project settings.</li>}
                        {!hasItems && <li>Add items to the project.</li>}
                        {!instructions && <li>Add instructions for participants (optional).</li>}
                        {isDraft && <li>When ready, publish the project.</li>}
                        {isClosed && <li>Reopen this project to let participants annotate again.</li>}
                        {isArchived && <li>Unarchive/reopen if you want participants to see this project again.</li>}
                    </ul>
                    <div className={styles.emptyStateActions}>
                        <button
                            type="button"
                            className={styles.buttonPrimary}
                            onClick={() => navigate(`/projects/${folderId}`)}
                        >
                            ✎ Manage project
                        </button>
                    </div>
                </div>
            )}

            {/* Instructions */}
            {instructions ? (
                <div className={styles.instructionsCard}>
                    <h4 className={styles.sectionTitle}>Instructions</h4>
                    <p className={styles.instructionsText}>{instructions}</p>
                </div>
            ) : (
                <div className={styles.instructionsCard}>
                    <p className={styles.noInstructions}>
                        {isAdmin
                            ? 'No instructions have been provided for this project.'
                            : 'The project admin has not added instructions yet.'}
                    </p>
                </div>
            )}

            {/* Surveys */}
            {surveyLinks.length > 0 && (
                <div className={styles.actionCard}>
                    <h4 className={styles.sectionTitle}>Surveys</h4>
                    <ul className={styles.surveyLinkList}>
                        {surveyLinks.map((link) => {
                            const postTestLocked = !link.accessible
                                && link.type === SURVEY_TYPES.POST_TEST;
                            return (
                                <li key={link.survey.type} className={styles.surveyLinkRow}>
                                    <div className={styles.surveyLinkInfo}>
                                        <span className={styles.surveyTypeBadge}>{link.typeLabel}</span>
                                        <span className={styles.surveyLinkTitle}>{link.title}</span>
                                        {link.complete ? (
                                            <span className={styles.surveyCompleteBadge}>Completed</span>
                                        ) : null}
                                        {postTestLocked ? (
                                            <p className={styles.surveyLockedMessage}>
                                                Available once you have completed all images.
                                                Please return then to take this survey.
                                            </p>
                                        ) : null}
                                    </div>
                                    {link.accessible ? (
                                        <Link
                                            to={link.href}
                                            className={styles.surveyOpenLink}
                                        >
                                            {link.complete ? 'View' : 'Open'}
                                        </Link>
                                    ) : (
                                        <span className={styles.surveyLocked}>
                                            {postTestLocked ? 'Not yet available' : 'Unavailable'}
                                        </span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            {/* Annotator: one card — annotation task umbrella, Open top-right, then per-item progress */}
            {isAnnotator && (
                <div className={[
                    styles.actionCard,
                    preTestBlocksViewer && !projectNotReadyForParticipant
                        ? styles.actionCardDisabled
                        : '',
                ].filter(Boolean).join(' ')}
                >
                    {projectNotReadyForParticipant ? (
                        <>
                            <h4 className={styles.sectionTitle}>Annotation task</h4>
                            <p className={styles.actionDescription}>
                                {isDraft
                                    ? 'This project is being set up. Once published, it will appear with items to annotate.'
                                    : isClosed
                                        ? 'This project is closed. Annotation is currently disabled.'
                                        : isArchived
                                            ? 'This project is archived and not available for annotation.'
                                            : 'This project is not ready for participant annotation yet.'}
                            </p>
                        </>
                    ) : (
                        <>
                            <div className={styles.annotationTaskHeader}>
                                <h4 className={styles.sectionTitle}>Annotation task</h4>
                                <button
                                    type="button"
                                    className={styles.buttonOpen}
                                    onClick={() => openViewer(firstItemId)}
                                    disabled={!canOpen}
                                    title={
                                        preTestBlocksViewer
                                            ? 'Complete the pre-test first'
                                            : !firstItemId
                                                ? 'No items available yet'
                                                : 'Open annotation viewer'
                                    }
                                >
                                    Open ▶
                                </button>
                            </div>
                            {preTestBlocksViewer ? (
                                <p className={styles.preTestGateNote} role="status">
                                    Complete the pre-test survey before starting annotation.
                                </p>
                            ) : (
                                <p className={styles.actionDescription}>
                                    {firstItemId
                                        ? 'Open the annotation viewer to begin or resume your work.'
                                        : 'No items have been added to this project yet.'}
                                </p>
                            )}
                            {items.length > 0 && (
                                <>
                                    <h5 className={styles.annotationSubheading}>Your progress</h5>
                                    {!preTestBlocksViewer ? (
                                        <p className={styles.actionDescription}>
                                            Click an item to open it in the annotation viewer.
                                        </p>
                                    ) : null}
                                    <div className={styles.progressListWrap}>
                                        <ProjectItemListWithProgress
                                            items={items}
                                            progressMap={progressMap}
                                            disabled={preTestBlocksViewer}
                                            onSelectItem={(item) => openViewer(item._id)}
                                        />
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Reviewer placeholder */}
            {isReviewer && !isAnnotator && (
                <div className={styles.actionCard}>
                    <h4 className={styles.sectionTitle}>Review task</h4>
                    <p className={styles.actionDescription}>
                        {projectNotReadyForParticipant
                            ? (isDraft
                                ? 'Items will appear here once the project is configured and published.'
                                : isClosed
                                    ? 'This project is closed. Review access is currently disabled.'
                                    : 'This project is archived and hidden from participant workflows.')
                            : 'Review functionality is coming soon.'}
                    </p>
                </div>
            )}

            {/* Admin-only: no other role */}
            {isAdmin && !isAnnotator && !isReviewer && (
                <div className={styles.actionCard}>
                    <p className={styles.actionDescription}>
                        You are a project admin. Use "Manage project" above to configure items and participants.
                    </p>
                </div>
            )}
        </div>
    );
}

export default ProjectHomepage;
